import { msg } from "@lingui/core/macro";
import { createSignal, Match, onCleanup, Show, Switch } from "solid-js";
import { createRoute, Link } from "@tanstack/solid-router";
import { CheckCircle2, Download, Loader2, RefreshCw, ShieldCheck, XCircle } from "@fleet/icons";
import { Card, CardContent, Label, PageContainer, PageHeader, TextField } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { appRoute } from "./route";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";
import {
  importSnapshot,
  importTransferKey,
  parseSnapshotJson,
  parseTransferCode,
  receivePayloadOverChannel,
  RECEIVER_INITIAL,
  type ReceiverEvent,
  receiverReducer,
} from "@fieldcheck/shared";
import { connectAsReceiver, type RtcPeerHandle } from "@/features/p2p-transfer/webrtc";
import {
  createP2pSignalingClient,
  mapTransferError,
  toSnapshotDb,
} from "@/features/p2p-transfer/support";
import { TransferErrorCard, TransferProgressBar } from "@/features/p2p-transfer/transfer-ui";

/**
 * RECEPTOR del traspaso dispositivo-a-dispositivo (P2P por QR).
 *
 * El usuario pega el código de traspaso (o escanea el QR con la cámara del
 * sistema y copia el texto). Con la parte pública se une a la sala de
 * señalización; con la clave (que el servidor nunca ve) se descifra todo.
 * La importación es transaccional e idempotente por id: re-recibir el mismo
 * snapshot no duplica nada y jamás sobreescribe datos locales.
 */
function TransferReceivePage() {
  const { t } = useI18n();
  const session = useSession();
  const powerSync = usePowerSyncContext();

  const [state, setState] = createSignal(RECEIVER_INITIAL);
  const dispatch = (event: ReceiverEvent) => setState((s) => receiverReducer(s, event));
  const [rawCode, setRawCode] = createSignal("");
  const [formatError, setFormatError] = createSignal(false);

  let peer: RtcPeerHandle | undefined;
  const signaling = createP2pSignalingClient();

  const cleanup = () => {
    peer?.close();
    peer = undefined;
  };
  onCleanup(cleanup);

  const busy = () => !["idle", "done", "error"].includes(state().phase);

  const connect = () => {
    if (busy()) {
      return;
    }
    const parsed = parseTransferCode(rawCode());
    if (!parsed) {
      setFormatError(true);
      return;
    }
    setFormatError(false);
    dispatch({ type: "RESET" });
    dispatch({ type: "JOIN" });
    void (async () => {
      try {
        const key = await importTransferKey(parsed.keyBytes);
        const { receiverToken } = await signaling.joinRoom(parsed.roomCode);
        dispatch({ type: "JOINED" });

        peer = connectAsReceiver({
          signaling,
          code: parsed.roomCode,
          receiverToken,
          key,
        });
        const channel = await peer.channelPromise;
        dispatch({ type: "CHANNEL_OPEN" });

        const counts = await receivePayloadOverChannel(channel, key, {
          onProgress: (received, total) =>
            dispatch({ type: "PROGRESS", transferredBytes: received, totalBytes: total }),
          importPayload: async (json) => {
            dispatch({ type: "IMPORT_STARTED" });
            const snapshot = parseSnapshotJson(json);
            if (!snapshot) {
              throw new Error("invalid snapshot");
            }
            const db = powerSync.db();
            if (!db) {
              throw new Error("local database unavailable");
            }
            return importSnapshot(toSnapshotDb(db), snapshot, session.userId());
          },
        });
        dispatch({ type: "IMPORTED", inserted: counts.inserted, skipped: counts.skipped });
      } catch (error) {
        dispatch({ type: "FAIL", kind: mapTransferError(error) });
      } finally {
        cleanup();
      }
    })();
  };

  const reset = () => {
    cleanup();
    setRawCode("");
    setFormatError(false);
    dispatch({ type: "RESET" });
  };

  const percent = () => {
    const current = state();
    if (current.phase !== "receiving" || current.progress.totalBytes === 0) {
      return 0;
    }
    return Math.round((current.progress.transferredBytes / current.progress.totalBytes) * 100);
  };

  // Accessors que estrechan el tipo por fase (evitan aserciones en los <Match>).
  const doneState = () => {
    const s = state();
    return s.phase === "done" ? s : undefined;
  };
  const errorState = () => {
    const s = state();
    return s.phase === "error" ? s : undefined;
  };

  return (
    <PageContainer maxWidth="2xl" class="space-y-8" containerClass="scrollbar-thin">
      <div data-testid="p2p-receive-page">
        <PageHeader
          title={t(msg`Receive data`)}
          description={t(msg`Import data sent from your other device, end-to-end encrypted.`)}
          backTo="/profile"
          icon={Download}
        />
      </div>

      <Card>
        <CardContent class="space-y-6 p-6">
          <div class="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
            <ShieldCheck class="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <p class="text-xs text-muted-foreground">
              {t(
                msg`On the sending device open "Transfer to another device", then scan its QR code with your camera and copy the text here — or just paste the transfer code. Nothing is imported twice and your existing data is never overwritten.`,
              )}
            </p>
          </div>

          <Switch>
            <Match when={state().phase === "idle"}>
              <div class="space-y-4" data-testid="p2p-receive-form">
                <div class="space-y-2">
                  <Label for="p2p-code-input" class="text-xs font-semibold">
                    {t(msg`Transfer code`)}
                  </Label>
                  <TextField
                    id="p2p-code-input"
                    autocomplete="off"
                    autocapitalize="none"
                    spellcheck={false}
                    // oxlint-disable-next-line lingui/no-unlocalized-strings -- prefijo literal del código, no es prosa
                    placeholder="FCHK1-…"
                    value={rawCode()}
                    onInput={(e) => {
                      setRawCode(e.currentTarget.value);
                      setFormatError(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        connect();
                      }
                    }}
                    data-testid="p2p-code-input"
                  />
                  <Show when={formatError()}>
                    <p class="text-xs text-destructive" data-testid="p2p-code-format-error">
                      {t(msg`That transfer code doesn't look right. Check it and try again.`)}
                    </p>
                  </Show>
                </div>
                <Button
                  class="h-12 w-full gap-2 rounded-2xl"
                  onClick={connect}
                  disabled={rawCode().trim().length === 0}
                  data-testid="p2p-connect-button"
                >
                  <Download class="h-5 w-5" />
                  {t(msg`Connect and receive`)}
                </Button>
              </div>
            </Match>

            <Match when={state().phase === "joining" || state().phase === "connecting"}>
              <div
                class="flex flex-col items-center gap-3 py-10 text-muted-foreground"
                data-testid="p2p-receive-status"
              >
                <Loader2 class="h-8 w-8 animate-spin" />
                <p class="text-sm">{t(msg`Connecting to the sending device…`)}</p>
              </div>
            </Match>

            <Match when={state().phase === "receiving"}>
              <div class="space-y-4 py-6" data-testid="p2p-receive-status">
                <p class="text-center text-sm font-medium">
                  {t(msg`Receiving data… {percent}%`, { percent: String(percent()) })}
                </p>
                <TransferProgressBar percent={percent()} />
              </div>
            </Match>

            <Match when={state().phase === "importing"}>
              <div
                class="flex flex-col items-center gap-3 py-10 text-muted-foreground"
                data-testid="p2p-receive-status"
              >
                <Loader2 class="h-8 w-8 animate-spin" />
                <p class="text-sm">{t(msg`Importing into this device…`)}</p>
              </div>
            </Match>

            <Match when={doneState()} keyed>
              {(done) => (
                <div
                  class="flex flex-col items-center gap-4 py-8 text-center"
                  data-testid="p2p-receive-done"
                >
                  <CheckCircle2 class="h-12 w-12 text-primary" />
                  <div class="space-y-1">
                    <p class="text-base font-bold">{t(msg`Data received`)}</p>
                    <p class="text-sm text-muted-foreground">
                      {t(
                        msg`{inserted} items imported, {skipped} skipped (already on this device).`,
                        {
                          inserted: String(done.inserted),
                          skipped: String(done.skipped),
                        },
                      )}
                    </p>
                  </div>
                  <Link
                    to="/inspections"
                    class="text-sm font-semibold text-primary hover:underline"
                    data-testid="p2p-receive-go-inspections"
                  >
                    {t(msg`See my inspections`)}
                  </Link>
                </div>
              )}
            </Match>

            <Match when={errorState()} keyed>
              {(failed) => (
                <div class="space-y-4" data-testid="p2p-receive-error">
                  <TransferErrorCard kind={failed.kind} icon={XCircle} />
                  <Button
                    variant="outline"
                    class="h-11 w-full gap-2 rounded-xl"
                    onClick={reset}
                    data-testid="p2p-receive-retry"
                  >
                    <RefreshCw class="h-4 w-4" />
                    {t(msg`Try another code`)}
                  </Button>
                </div>
              )}
            </Match>
          </Switch>
        </CardContent>
      </Card>
    </PageContainer>
  );
}

export const profileTransferReceiveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile/receive-data",
  component: TransferReceivePage,
});
